import React, { useState, useEffect, useRef } from "react";
import { useNavigate, type NavigateOptions } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/button";

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidSession, setIsValidSession] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const isFormValid = Boolean(
    newPassword && confirmPassword && newPassword === confirmPassword
  );

  // TAMBAHKAN BARIS INI
  const toastShownRef = useRef(false);

  const handleNavigateToAuth = async (options?: NavigateOptions) => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("❌ Error signing out before navigating to auth:", error);
    } finally {
      sessionStorage.removeItem("isResettingPassword");
      navigate("/auth", options);
    }
  };

  useEffect(() => {
    sessionStorage.setItem("isResettingPassword", "true");

    const checkSession = async () => {
      try {
        setIsVerifying(true);

        // Tunggu fragment dari Supabase diproses
        await new Promise(resolve => setTimeout(resolve, 1500));

        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error || !session) {
          toast.error("Link reset password tidak valid atau sudah kadaluarsa");
          sessionStorage.removeItem("isResettingPassword");
          setTimeout(() => navigate("/auth"), 2500);
          return;
        }

        // Jika session valid
        setIsValidSession(true);
        if (!toastShownRef.current) {
          // ⬅️ Pastikan toast cuma muncul sekali
          toast.success("Silakan masukkan password baru Anda", {
            duration: 3000,
          });
          toastShownRef.current = true;
        }
      } catch (err) {
        console.error("💥 Error verifying session:", err);
        toast.error("Terjadi kesalahan. Silakan coba lagi.");
        sessionStorage.removeItem("isResettingPassword");
        setTimeout(() => navigate("/auth"), 2500);
      } finally {
        setIsVerifying(false);
      }
    };

    checkSession();

    return () => {
      sessionStorage.removeItem("isResettingPassword");
    };
  }, [navigate]);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword.trim()) {
      toast.error("Password tidak boleh kosong");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Password tidak cocok!");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        console.error("❌ Password update error:", error);
        toast.error("Gagal memperbarui password. Silakan coba lagi.");
        setLoading(false);
        return;
      }

      toast.success("Password berhasil diperbarui!", { duration: 1500 });

      // Bersihkan session storage
      sessionStorage.removeItem("isResettingPassword");

      // Tunggu sebentar agar toast terlihat
      await new Promise(resolve => setTimeout(resolve, 500));

      // Sign out untuk clear recovery session
      const { error: signOutError } = await supabase.auth.signOut();

      if (signOutError) {
        console.error("❌ Sign out error:", signOutError);
      }

      // Navigate ke auth dengan replace
      navigate("/auth", { replace: true });
    } catch (err) {
      console.error("❌ Error updating password:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Terjadi kesalahan saat memperbarui password.";
      toast.error(errorMessage);
    } finally {
      // Pastikan loading state di-reset
      setLoading(false);
    }
  };

  // LOADING STATE
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#eeeff6]">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primarycolor-1 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">
            Memverifikasi link reset password...
          </p>
          <p className="text-sm text-gray-500 mt-2">Mohon tunggu sebentar</p>
        </div>
      </div>
    );
  }

  // INVALID SESSION STATE
  if (!isValidSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#eeeff6]">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Link Tidak Valid
          </h2>
          <p className="text-gray-600 mb-6">
            Link reset password tidak valid atau sudah kadaluarsa.
          </p>
          <Button
            type="button"
            onClick={() => {
              void handleNavigateToAuth();
            }}
            className="w-full h-12 bg-[#e4e6ff] text-[#6b2cfe] font-medium rounded-xl hover:bg-[#d1d5ff] transition-all duration-300"
          >
            Kembali ke halaman login
          </Button>
        </div>
      </div>
    );
  }

  // RESET PASSWORD FORM

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#eeeff6] p-4">
      <div className="flex flex-col items-center mb-6">
        <img
          className="w-16 h-16 object-cover mb-2"
          alt="AdapTeach Logo"
          src="/logo.png"
        />
        <h2 className="text-2xl font-bold text-[#373e46]">AdapTeach</h2>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Reset Password
          </h1>
          <p className="text-sm text-gray-600">
            Masukkan password baru Anda. Password minimal 6 karakter.
          </p>
        </div>

        <form onSubmit={handleReset} className="space-y-4">
          {/* Input Password Baru */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password Baru
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Minimal 6 karakter"
                className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primarycolor-1 focus:border-transparent outline-none transition-all"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Input Konfirmasi Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Konfirmasi Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Ketik ulang password"
                className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primarycolor-1 focus:border-transparent outline-none transition-all"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Indikator kekuatan password */}
          {newPassword && (
            <div className="text-xs text-gray-600">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className={`flex-1 h-1.5 rounded-full ${
                    newPassword.length >= 8
                      ? "bg-green-500"
                      : newPassword.length >= 6
                        ? "bg-yellow-500"
                        : "bg-red-500"
                  }`}
                />
              </div>
              <p>
                Kekuatan password:{" "}
                {newPassword.length >= 8
                  ? "Kuat"
                  : newPassword.length >= 6
                    ? "Cukup"
                    : "Lemah"}
              </p>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || !isFormValid}
            className={`w-full h-12 font-semibold rounded-xl transition-all duration-300 ease-in-out ${
              isFormValid && !loading
                ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Memproses...
              </div>
            ) : (
              "Simpan Password Baru"
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Button
            type="button"
            onClick={() => {
              void handleNavigateToAuth();
            }}
            className="w-full h-12 bg-[#e4e6ff] text-[#6b2cfe] font-medium rounded-xl hover:bg-[#d1d5ff] transition-all duration-300"
          >
            Kembali ke halaman login
          </Button>
        </div>
      </div>
    </div>
  );
};
